import React from 'react';
import { Button, Display, Modal, Spinner } from 'design/ui';
import { textSize } from 'design/tokens';

export type LinkPhase = 'connecting' | 'syncing' | 'done' | 'error';

export interface LinkProgress {
  itemId: string | null;
  institution: string;
  accounts: number;
  /** Transactions the sync passes have added so far, as the API counted them. */
  transactions: number;
  phase: LinkPhase;
  error?: string;
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * What happens after Plaid Link closes. Without it the Plaid sheet vanished and
 * nothing said whether the bank came through, the new transactions only showed
 * after a reload, and adding a second bank meant finding the menu again.
 */
export const LinkedDialog: React.FC<{
  progress: LinkProgress;
  onLinkAnother: () => void;
  onClose: () => void;
}> = ({ progress, onLinkAnother, onClose }) => {
  const { institution, accounts, transactions, phase } = progress;

  const title =
    phase === 'connecting'
      ? `Connecting ${institution}`
      : phase === 'error'
        ? `${institution} did not connect`
        : `${institution} is connected`;

  const body =
    phase === 'connecting'
      ? 'Saving your accounts. This takes a few seconds.'
      : phase === 'error'
        ? progress.error || 'Something went wrong while saving the connection. Try linking it again.'
        : phase === 'syncing'
          ? `${plural(accounts, 'account', 'accounts')} added. Bringing in transactions${
              transactions > 0 ? `: ${transactions.toLocaleString()} so far` : ''
            }. The first pull can take a minute.`
          : transactions > 0
            ? `${plural(accounts, 'account', 'accounts')} and ${plural(transactions, 'transaction', 'transactions')} are in.`
            : `${plural(accounts, 'account', 'accounts')} added. ${institution} has not sent transactions yet; they will appear with the next daily refresh.`;

  const busy = phase === 'connecting' || phase === 'syncing';

  return (
    <Modal onClose={onClose} width={440} label="Account connection">
      <div style={{ padding: '30px 32px 26px' }}>
        <Display as="h3" style={{ marginBottom: 12 }}>
          {title}
        </Display>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 24 }}>
          {busy && <Spinner />}
          <p style={{ fontSize: textSize.medium, lineHeight: 1.65, color: 'var(--st-ink3)', margin: 0 }}>{body}</p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          {phase !== 'connecting' && (
            <Button variant="secondary" onClick={onLinkAnother}>
              {phase === 'error' ? 'Try again' : 'Link another account'}
            </Button>
          )}
          <Button variant={busy ? 'secondary' : 'primary'} onClick={onClose}>
            {busy ? 'Close' : 'Done'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default LinkedDialog;
